import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import { Link as RouterLink } from 'react-router-dom';

const currentCapabilities = [
  'Les visiteurs non connectés peuvent saisir leur propre adresse email depuis la page d accueil pour accepter de recevoir des informations Jacana.',
  'Les utilisateurs connectés utilisent automatiquement l adresse email de leur compte pour cette inscription, sans pouvoir la remplacer par une autre.',
  'Un utilisateur déjà abonné ne voit plus l invitation newsletter sur la page d accueil.',
  'Jacana enregistre cette autorisation de communication afin de garder une trace claire du consentement donné.',
  'L application conserve un historique de cette autorisation pour savoir si une personne a bien accepté les communications.',
  'Le formulaire est protégé contre certains abus simples afin d éviter des envois répétitifs ou manifestement anormaux.',
  'Quand un utilisateur est authentifié, le consentement marketing est rattaché à son compte utilisateur.',
  'Le backend bloque déjà les tentatives consistant à inscrire une adresse différente de celle du compte connecté.',
  'Un utilisateur connecté peut gérer ses préférences marketing depuis son espace personnel.',
  'Il existe donc une liste des personnes ayant accepté de recevoir des communications autour de Jacana.',
  'Jacana peut déjà utiliser cette liste pour informer manuellement les personnes concernées lors de la publication d une nouvelle sortie.',
  'Les annonces de sortie peuvent aussi être envoyées manuellement depuis l administration aux personnes joignables par notification.',
];

const notYetImplemented = [
  'Jacana n envoie pas automatiquement de newsletters à ce stade.',
  'Il n existe pas encore d outil de campagne marketing complet dans l administration.',
  'Les annonces de sortie restent des envois manuels, déclenchés volontairement par Jacana.',
  'Il n existe pas encore de gestion avancée des abonnés ou de classement marketing détaillé.',
  'Il n existe pas encore de statistiques marketing comme les ouvertures ou les clics.',
  'Il n existe pas encore de système avancé de désinscription automatique.',
  'Les emails ne sont pas synchronisés automatiquement avec Mailchimp, Brevo ou une autre plateforme newsletter.',
];

const currentUses = [
  'Jacana peut consulter et exploiter la liste des personnes ayant accepté de recevoir des communications.',
  'Jacana peut envoyer manuellement une annonce lorsqu une sortie publiée doit être mise en avant.',
  'Cela peut servir à annoncer de nouvelles sorties nature.',
  'Cela peut servir à informer sur des événements ou temps forts associatifs.',
  'Cela peut servir à communiquer des nouveautés dans l offre Jacana.',
  'Cela peut servir à envoyer des rappels saisonniers utiles au public intéressé.',
];

const futureIdeas = [
  'Synchronisation avec une plateforme newsletter externe.',
  'Création de campagnes email directement dans Jacana.',
  'Statistiques d ouverture et de clics.',
  'Segmentation des abonnés selon leurs centres d intérêt.',
  'Désinscription automatique plus avancée.',
  'Double validation email pour confirmer l inscription.',
  'Newsletters automatiques selon des règles définies.',
  'Programmation d envois à une date précise.',
];

function InfoList({ items, icon }: { items: string[]; icon: React.ReactNode }) {
  return (
    <List disablePadding>
      {items.map((item) => (
        <ListItem key={item} disableGutters alignItems="flex-start" sx={{ py: 0.75 }}>
          <ListItemIcon sx={{ minWidth: 34, color: 'primary.main', mt: 0.3 }}>{icon}</ListItemIcon>
          <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1', color: 'text.primary' }} />
        </ListItem>
      ))}
    </List>
  );
}

export default function NewsletterInfoPage() {
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 14, md: 18 } }}>
      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.18em' }}
            >
              Administration
            </Typography>
            <Typography variant="h3" component="h1" sx={{ mt: 1, fontStyle: 'italic' }}>
              Info Newsletter
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: 780 }}>
              Cette page explique simplement ce que Jacana gère aujourd'hui autour des inscriptions
              à la newsletter, ce qui reste volontairement simplifié, et les évolutions qui pourraient
              être ajoutées plus tard si le besoin se confirme.
            </Typography>
          </Box>

          <Button component={RouterLink} to="/admin" variant="outlined" color="primary">
            Retour au tableau de bord
          </Button>
        </Stack>

        <Alert icon={<InfoOutlinedIcon fontSize="inherit" />} severity="info" variant="outlined">
          Les fonctionnalités newsletter sont aujourd hui simples et volontairement cadrées.
          Jacana peut déjà prévenir manuellement son public dans certains cas, sans disposer encore
          d un outil marketing complet.
        </Alert>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <MailOutlineOutlinedIcon color="primary" />
              <Typography variant="h5">Vue d ensemble</Typography>
              <Chip label="Page informative" color="primary" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Aujourd'hui, Jacana permet surtout de recueillir et conserver un accord de communication.
              En pratique, cela permet déjà de constituer un public joignable et d envoyer manuellement
              certaines annonces utiles, par exemple lorsqu une nouvelle sortie est publiée. En revanche,
              Jacana ne dispose pas encore d un outil complet de campagne newsletter.
            </Typography>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CheckCircleOutlineIcon color="success" />
                <Typography variant="h5">Ce qui est actuellement en place</Typography>
              </Stack>
              <InfoList items={currentCapabilities} icon={<CheckCircleOutlineIcon fontSize="small" />} />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              flex: 1,
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <PendingActionsOutlinedIcon color="warning" />
                <Typography variant="h5">Fonctionnalités non encore implémentées</Typography>
              </Stack>
              <InfoList items={notYetImplemented} icon={<RadioButtonUncheckedIcon fontSize="small" />} />
            </Stack>
          </Paper>
        </Stack>

        <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Accordion defaultExpanded disableGutters sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <TipsAndUpdatesOutlinedIcon color="primary" />
                <Typography variant="h6">Utilisations possibles actuelles</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: { xs: 2, md: 3 }, pb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Même avec un fonctionnement encore simple, les accords déjà collectés permettent
                à Jacana de faire des communications utiles et ponctuelles autour de son activité.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoList items={currentUses} icon={<ArrowForwardOutlinedIcon fontSize="small" />} />
            </AccordionDetails>
          </Accordion>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: '1px dashed',
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TrendingUpOutlinedIcon color="primary" />
              <Typography variant="h5">Évolutions possibles</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Les éléments ci-dessous ne sont pas prévus automatiquement dans cette version. Ce sont
              des fonctionnalités qui pourraient être ajoutées ultérieurement sur demande.
            </Typography>
            <InfoList items={futureIdeas} icon={<ArrowForwardOutlinedIcon fontSize="small" />} />
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}